import { useState, useCallback, Suspense, lazy } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { useHierarchyForecast, useUnassignedRequestIDs } from '../hooks'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft, ClipboardList, PlayCircle, CheckCircle2, AlertCircle, List, Notebook, Table2, Network } from 'lucide-react'
import type { HierarchyForecastRow } from '../api'
import { recoverRequestsApi } from '../api'
import { formatDateForLocale } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import { RunbookViewDialog } from '../components/RunbookViewDialog'
import { getStatusDotClass, getStatusPillClass } from '../components/RecoveryTrackingHeader'
import { buildIconPath } from '@/lib/imagePaths'
import { TranslatableText } from '@/components/ui/TranslatableText'
import { UserNameWithPresence } from '@/components/ui/UserNameWithPresence'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'

const VisualizationApp = lazy(() => import('../../visualization/_legacy/App'))
const AppStateProvider = lazy(() =>
    import('../../visualization/_legacy/context/AppStateContext').then((m) => ({
        default: m.AppStateProvider,
    }))
)

function DoughnutChart({ percentage, size = 32 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }} title={`${Math.round(pct)}%`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={3} className="text-muted-foreground/30" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">{Math.round(pct)}%</span>
        </div>
    )
}

function TaskStatsCell({ row }: { row: HierarchyForecastRow }) {
    const { t } = useTranslation()
    const noTasks = row.totalTasks ?? 0
    if (noTasks === 0) {
        return <span className="text-sm text-muted-foreground">{t('recoverRequests.noRunbook')}</span>
    }
    const noStarted = row.startedTasks ?? 0
    const noCompleted = row.completedTasks ?? 0
    const noIssues = row.issueTasks ?? 0
    const pct = row.percentComplete ?? Math.round((noCompleted / noTasks) * 100)
    return (
        <div className="flex items-center gap-2 flex-nowrap">
            <DoughnutChart percentage={pct} size={36} />
            <div className="flex items-center gap-1.5 flex-shrink-0">
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-primary/10">
                    <ClipboardList className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-medium">{noTasks}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/10">
                    <PlayCircle className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-xs font-medium">{noStarted}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-green-500/10">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-xs font-medium">{noCompleted}</span>
                </div>
                <div className="flex items-center gap-1 rounded px-1.5 py-0.5 bg-red-500/10">
                    <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                    <span className="text-xs font-medium">{noIssues}</span>
                </div>
            </div>
        </div>
    )
}

function isEngineerAssigned(row: HierarchyForecastRow): boolean {
    const status = (row.assignmentStatus ?? '').toLowerCase()
    const hasEngineer = !!(row.engineerName ?? '').trim()
    return hasEngineer || status === 'assigned' || status === 'inprogress' || status === 'in progress'
}

export function UnassignedRequestDetailPage() {
    const { t, i18n } = useTranslation()
    const locale = i18n.language || 'en-US'
    const { requestID: requestIDParam } = useParams<{ requestID: string }>()
    const navigate = useNavigate()
    const { data: rows = [], isLoading } = useHierarchyForecast(requestIDParam ?? null)
    const { data: unassignedData } = useUnassignedRequestIDs({ page: 1, limit: 500 })
    const [viewMode, setViewMode] = useState<'table' | 'canvas'>('table')
    const [objectRunbookDialog, setObjectRunbookDialog] = useState<{ objectID: string; forecastStartTime: string | null } | null>(null)
    const [updatesDialogRecoverRequestId, setUpdatesDialogRecoverRequestId] = useState<string | null>(null)

    const mainObjectId = rows.length > 0 ? (rows[0].objectID ?? null) : null
    // Find the scenario for this request from the unassigned list
    const matchingRequest = (unassignedData?.list ?? []).find(r => r.requestID === requestIDParam)
    const requestScenarioId = matchingRequest?.scenario ? String(matchingRequest.scenario) : '1'

    const handleViewRunbook = useCallback(
        (row: HierarchyForecastRow) => {
            const objectID = row.objectID ?? ''
            if (!objectID) return
            if (isEngineerAssigned(row)) {
                const runbookId = row.recoverRequestID != null && row.recoverRequestID > 0
                    ? String(row.recoverRequestID)
                    : requestIDParam
                if (!runbookId) return
                navigate(`/recovery-runbook/${encodeURIComponent(runbookId)}`, {
                    state: {
                        objectID: row.objectID,
                        objectDescription: row.objectDescription,
                        forecastStartTime: row.forecastStartTime,
                        from: `/recovery-management/requests/${requestIDParam}`,
                        readOnly: true,
                    },
                })
            } else {
                setObjectRunbookDialog({
                    objectID,
                    forecastStartTime: row.forecastStartTime ?? null,
                })
            }
        },
        [navigate, requestIDParam]
    )

    const { data: updatesData } = useQuery({
        queryKey: ['recovery-assignments', updatesDialogRecoverRequestId],
        queryFn: async () => {
            const idToFetch = updatesDialogRecoverRequestId ?? ''
            if (!idToFetch) return []
            const res = await recoverRequestsApi.getRecoveryAssignments(idToFetch)
            const body = res?.data ?? res
            if (!body) return []
            const inner = body.data ?? body
            const list = Array.isArray(inner) ? inner : (inner?.assignments ?? [])
            return Array.isArray(list) ? list : []
        },
        enabled: !!updatesDialogRecoverRequestId,
        refetchOnMount: true,
    })
    const updatesList = Array.isArray(updatesData) ? updatesData : []

    function formatDateShort(date: Date | string): string {
        const d = typeof date === 'string' ? new Date(date) : date
        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })
    }

    return (
        <AdminRoute>
            <div className="p-6 space-y-4">
                <Button variant="ghost" size="sm" className="w-fit" onClick={() => navigate('/recovery-management')}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t('common.back')}
                </Button>
                <div>
                    <h1 className="text-xl font-bold truncate">
                        {t('recoverRequests.unassignedRequestDetail', 'Unassigned Request')}
                        {rows.length > 0 ? (
                            <>: {rows[0].objectID ?? '—'} — {rows[0].forecastStartTime ? formatDateForLocale(rows[0].forecastStartTime, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</>
                        ) : (
                            <>: {requestIDParam ?? '—'}</>
                        )}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('recoverRequests.hierarchyForecastDescription', 'Recovery hierarchy forecast for this request.')}
                    </p>
                </div>

                <Tabs value={viewMode} onValueChange={(v) => setViewMode(v === 'canvas' ? 'canvas' : 'table')} className="w-full">
                    <TabsList className="grid w-full max-w-[200px] grid-cols-2">
                        <TabsTrigger value="table" className="flex items-center gap-2">
                            <Table2 className="h-4 w-4 text-blue-600" />
                            {t('recoverRequests.tableView', 'Table')}
                        </TabsTrigger>
                        <TabsTrigger value="canvas" className="flex items-center gap-2" disabled={!mainObjectId}>
                            <Network className="h-4 w-4 text-blue-600" />
                            {t('recoverRequests.canvasView', 'Canvas')}
                        </TabsTrigger>
                    </TabsList>

                <TabsContent value="table" className="mt-4">
                {isLoading ? (
                    <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">{t('common.loading', 'Loading…')}</div>
                ) : rows.length === 0 ? (
                    <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                        {t('recoverRequests.noHierarchyForecast', 'No hierarchy forecast data for this request.')}
                    </div>
                ) : (
                    <div className="rounded-lg border bg-card overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]">
                            <thead>
                                <tr className="border-b bg-muted/50">
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.objectID', 'Object ID')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.waitGo', 'Wait/Go')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.forecastStartTime', 'Forecast start')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.forecastFinishTime', 'Forecast finish')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.isComplete', 'IsComplete')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.actualStartTime', 'Actual start time')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.tasksProgress', 'Tasks / Progress')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.engineerName', 'Engineer name')}</th>
                                    <th className="text-left p-3 font-medium">{t('recoverRequests.assignmentStatus', 'Assignment status')}</th>
                                    <th className="text-right p-3 font-medium">{t('common.actions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => (
                                    <tr key={row.recoveryHierarchyId ?? `${row.requestID}-${row.objectID}-${idx}`} className="border-b last:border-0 hover:bg-muted/30">
                                        <td className="p-3">
                                        <div className="flex items-center gap-2 min-w-0" title={[row.objectID, row.objectDescription].filter(Boolean).join(' — ') || undefined}>
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <span className="text-sm font-medium truncate">{row.objectID ?? '—'}</span>
                                                {row.objectDescription != null && row.objectDescription !== '' && (
                                                    <span className="text-xs text-muted-foreground truncate"><TranslatableText text={row.objectDescription} /></span>
                                                )}
                                            </div>
                                            {row.iconFilename && (
                                                <img src={buildIconPath(row.iconFilename)} alt="" className="h-7 w-7 object-contain shrink-0" />
                                            )}
                                        </div>
                                    </td>
                                        <td className="p-3">
                                            <span
                                                className={cn(
                                                    'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                                                    (row.waitGo?.toLowerCase() ?? '') === 'go' ? 'bg-green-500/20 text-green-800 dark:text-green-200' : 'bg-red-500/20 text-red-800 dark:text-red-200'
                                                )}
                                            >
                                                {row.waitGo === 'Go' || (row.waitGo?.toLowerCase() ?? '') === 'go' ? 'Go' : 'Wait'}
                                            </span>
                                        </td>
                                        <td className="p-3 text-muted-foreground text-xs">
                                            {row.forecastStartTime ? formatDateForLocale(row.forecastStartTime, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                        </td>
                                        <td className="p-3 text-muted-foreground text-xs">
                                            {row.forecastFinishTime ? formatDateForLocale(row.forecastFinishTime, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                        </td>
                                        <td className="p-3">
                                            {row.isComplete === 1 ? (
                                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-800 dark:text-green-200">
                                                    {t('recoverRequests.complete', 'Complete')}
                                                </span>
                                            ) : (
                                                '—'
                                            )}
                                        </td>
                                        <td className="p-3 text-muted-foreground text-xs">
                                            {row.actualStartTime ? formatDateForLocale(row.actualStartTime, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                        </td>
                                        <td className="p-3">
                                            <TaskStatsCell row={row} />
                                        </td>
                                        <td className="p-3 text-muted-foreground">{row.engineerName ? <TranslatableText text={row.engineerName} /> : '—'}</td>
                                        <td className="p-3 text-muted-foreground">{row.assignmentStatus ?? '—'}</td>
                                        <td className="p-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {(row.totalTasks ?? 0) > 0 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleViewRunbook(row)
                                                        }}
                                                        aria-label={t('recoverRequests.viewRunbook', 'View Runbook')}
                                                    >
                                                        <List className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        const id = row.recoverRequestID != null && row.recoverRequestID > 0
                                                            ? String(row.recoverRequestID)
                                                            : requestIDParam ?? ''
                                                        if (id) setUpdatesDialogRecoverRequestId(id)
                                                    }}
                                                    aria-label={t('recoverRequests.updates', 'Updates')}
                                                >
                                                    <Notebook className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                </TabsContent>

                <TabsContent value="canvas" className="mt-4">
                    {mainObjectId ? (
                        <div className="rounded-lg border bg-card overflow-hidden h-[600px] w-full">
                            <Suspense fallback={
                                <div className="flex h-full items-center justify-center">
                                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                                </div>
                            }>
                                <AppStateProvider>
                                    <ReactFlowProvider>
                                        <VisualizationApp
                                            scenario="recovery"
                                            view="dependency"
                                            objectId={mainObjectId}
                                            requestId={requestIDParam ?? undefined}
                                            showMainPalette={false}
                                            scenarioId={requestScenarioId}
                                            serviceId={requestScenarioId}
                                        />
                                    </ReactFlowProvider>
                                </AppStateProvider>
                            </Suspense>
                        </div>
                    ) : (
                        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                            {t('recoverRequests.noHierarchyForecast', 'No hierarchy forecast data for this request.')}
                        </div>
                    )}
                </TabsContent>
                </Tabs>

                {objectRunbookDialog && (
                    <RunbookViewDialog
                        open={!!objectRunbookDialog}
                        onOpenChange={(open) => !open && setObjectRunbookDialog(null)}
                        objectID={objectRunbookDialog.objectID}
                        scenario={null}
                        forecastStartTime={objectRunbookDialog.forecastStartTime}
                    />
                )}
                <Dialog open={!!updatesDialogRecoverRequestId} onOpenChange={(open) => !open && setUpdatesDialogRecoverRequestId(null)}>
                    <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Notebook className="h-5 w-5 text-blue-600" />
                                {t('recoverRequests.updates', 'Updates')}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="overflow-auto flex-1 min-h-0 -mx-6 px-6">
                            {updatesList.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4">{t('recoverRequests.noRecoveryUpdates', 'No recovery updates yet.')}</p>
                            ) : (
                                <table className="w-full text-sm border-collapse">
                                    <thead>
                                        <tr className="border-b bg-muted/50">
                                            <th className="text-left py-2 px-3 font-semibold text-gray-600 w-8" scope="col">Status</th>
                                            <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap" scope="col">Action</th>
                                            <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap w-36" scope="col">Date & time</th>
                                            <th className="text-left py-2 px-3 font-semibold text-gray-600 whitespace-nowrap" scope="col">User</th>
                                            <th className="text-left py-2 px-3 font-semibold text-gray-600 min-w-[8rem]" scope="col">{t('recoverRequests.engineerUpdate', 'Engineer update')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {updatesList.map((u) => {
                                            const dotClass = getStatusDotClass(u.action ?? '')
                                            const pillClass = getStatusPillClass(u.action ?? '')
                                            return (
                                                <tr key={u.id ?? `${u.actiondatetime}-${u.userName}`} className="border-b last:border-0">
                                                    <td className="py-2 px-3 align-middle">
                                                        <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotClass}`} aria-hidden="true" />
                                                    </td>
                                                    <td className="py-2 px-3">
                                                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${pillClass}`}>{u.action ?? '—'}</span>
                                                    </td>
                                                    <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{u.actiondatetime ? formatDateShort(u.actiondatetime) : '—'}</td>
                                                    <td className="py-2 px-3 text-gray-600">{u.userID ? <UserNameWithPresence userId={Number(u.userID)} name={u.userName ?? '—'} /> : (u.userName ?? '—')}</td>
                                                    <td className="py-2 px-3 text-gray-700">
                                                        {u.update ? (
                                                            <div className="prose prose-sm max-w-none [&_ul]:list-disc [&_ol]:list-decimal [&_p]:mb-1 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: u.update }} />
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminRoute>
    )
}
